'use client';

import { useTheme } from '@emotion/react';

import { Dispatch, ReactNode, SetStateAction, useEffect, Ref } from 'react';
import {
  DEVICE_SCREENS_MAX_STRING,
  filterDateArr,
  filterDates,
  filterPriceArr,
} from '../../../lib/consts';
import { Container } from '../../layout/Container';
import { BreadCrumbs } from '../../blocks/BreadCrumbs';
import CatalogFilter from '../../catalog-filter';
import CloseSVG from '../../../public/img/close.svg';
import { H1, H3, H4 } from '../../styled/typography';
import {
  CollapsableRotateAnimate,
  CropText,
} from '../../collapsable/collapsable';
import { GB_DOMAIN } from '../../../lib/api/apiUrl';
import { CustomCheckBox } from '../../form/form';
import {
  ButtonFilterCatalog,
  ButtonOrderingCatalog,
  ButtonShowMore,
} from '../../Buttons';
import Calendar from '../../calendar/calendar';
import { getEventTextFromCount } from '../../../utils/getEventTextFromCount';
import { PromoCard } from '../../promo-cards/promo-card';
import { PostCard } from '../../posts/post-card';
import { StandardButton } from '../../styled/Button';
import { Pagination } from '../../Pagination';
import LoupSVG from '../../../public/img/loupe.svg';
import { CatalogRecommends } from '../../blocks/CatalogRecommends';
import { UpButton } from '../../common/UpButton';
import { Subscription } from '../../about/Subscription';
import { MobileApps } from '../../about/MobileApps';
import { Action, City } from '../../../models';
import { Category, CategoryData } from '../../../models/action';
import { FilterQuery, ISortFilter } from '../../../models/filters';
import { PromoCardTypes } from '../../../models/promo-card';
import { useCookieAgreement } from '../../../providers';
import useOutsideClick from '../../../hooks/outside-click';

export function CatalogLayout({
  events,
  setIsNewData,
  getCountOfPage,
  seoCategory,
  productMicrodata,
  currentCity,
  getBreadCrumbsItems,
  showMobilesort,
  categories,
  sortTypes,
  eventCount,
  query,
  setQuery,
  setShowMobileSort,
  defaultQuery,
  setSeoCategory,
  setOpenedCategory,
  openedCategory,
  checkCategory,
  checkSubcategory,
  onChangeDateFilter,
  setCalendarIsOpen,
  calendarIsOpen,
  onCalendarDateChanged,
  dataForCalendarButtonIsLoading,
  onCalendarSubmit,
  calendarDates,
  totalCalendarPosts,
  getSeoParams,
  updateLoading,
  gorbiletRekomenduet,
  mobileFiltersCount,
}: {
  events: {
    next: string | null;
    count: number;
    results: Array<Action | PromoCardTypes>;
  };
  setIsNewData: (a: boolean) => void;
  getCountOfPage: (a: number) => number;
  seoCategory?: CategoryData | null;
  productMicrodata: ReactNode;
  currentCity: City;
  getBreadCrumbsItems: () => Array<{ title: string; url?: string }>;
  showMobilesort: boolean;
  categories: Array<Category>;
  sortTypes: Array<ISortFilter>;
  eventCount: number;
  query: FilterQuery;
  setQuery: (a: FilterQuery) => void;
  setShowMobileSort: (a: boolean) => void;
  defaultQuery: any;
  setSeoCategory: Dispatch<SetStateAction<CategoryData | null>>;
  setOpenedCategory: (a: number | null) => void;
  openedCategory: number | null;
  checkCategory: (a: Category, b: Array<CategoryData>) => void;
  checkSubcategory: (a: number, b: CategoryData) => void;
  onChangeDateFilter: (a?: number, b?: string, c?: string) => void;
  setCalendarIsOpen: (a: boolean) => void;
  calendarIsOpen: boolean;
  onCalendarDateChanged: (a: [string, string] | null) => void;
  dataForCalendarButtonIsLoading: boolean;
  onCalendarSubmit: (a: [string, string] | null) => void;
  calendarDates: [string, string] | null;
  totalCalendarPosts: number | null;
  getSeoParams: () => {
    title: string;
    description: string;
    h1: string;
    canonicalLink: string;
  };
  updateLoading: (a: boolean) => void;
  gorbiletRekomenduet: { actions: Action[]; slug?: string };
  mobileFiltersCount: number;
}) {
  const theme = useTheme();
  const isPromoCard = (x: any): x is PromoCardTypes => x.is_promo_card;
  const { title, h1 } = getSeoParams();
  const { showPopUp } = useCookieAgreement();

  const outsideRefCalendar = useOutsideClick(() => setCalendarIsOpen(false));

  useEffect(() => {
    document.title = title;
  }, [title]);

  return (
    <Container
      css={{
        padding: '15px 12px',
        [DEVICE_SCREENS_MAX_STRING.md]: {
          padding: '0px !important',
        },
      }}
    >
      {productMicrodata}
      <BreadCrumbs
        currentCity={currentCity}
        items={getBreadCrumbsItems()}
        css={{
          display: 'block',
          [DEVICE_SCREENS_MAX_STRING.sm]: {
            display: 'none',
          },
        }}
      />

      {showMobilesort ? (
        <CatalogFilter
          currentCity={currentCity}
          categories={categories}
          sortTypes={sortTypes}
          eventCount={eventCount}
          query={query}
          setQuery={setQuery}
          setShowMobileSort={setShowMobileSort}
          defaultQuery={defaultQuery}
          seoCategory={seoCategory ?? null}
          setSeoCategory={setSeoCategory}
        />
      ) : (
        <>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 24,
              [DEVICE_SCREENS_MAX_STRING.md]: {
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: '0 8px',
                marginTop: 10,
              },
            }}
          >
            <H1
              css={{
                fontSize: 44,
                color: '#000',
                fontFamily: 'Montserrat',
                fontWeight: 800,
                lineHeight: '46px',
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  lineHeight: 'normal',
                  fontSize: 24,
                  fontWeight: 900,
                },
              }}
            >
              {h1}
            </H1>
            <span
              css={{
                color: '#bfc1c3',
                display: 'none',
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  display: 'block',
                },
              }}
            >
              (выбрано {eventCount} акций)
            </span>
          </div>
          <main css={{ fontFamily: 'Golos', color: theme.colors.black }}>
            <div
              css={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                paddingTop: 40,
                [DEVICE_SCREENS_MAX_STRING.lg]: {
                  flexDirection: 'column',
                },
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  paddingTop: 14,
                },
              }}
            >
              <div
                css={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 20,
                  marginBottom: 20,
                  width: '25%',
                  [DEVICE_SCREENS_MAX_STRING.lg]: {
                    width: '100%',
                  },
                  [DEVICE_SCREENS_MAX_STRING.md]: {
                    display: 'none',
                  },
                }}
                data-testid='categoryColumn'
              >
                <button
                  type='button'
                  css={{
                    position: 'absolute',
                    left: 0,
                    top: '-24px',
                    color: '#BFC1C3',
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    updateLoading(true);
                    setQuery(defaultQuery);
                    setSeoCategory(null);
                    setOpenedCategory(null);
                  }}
                >
                  Сбросить фильтры{' '}
                  <CloseSVG css={{ width: 16, height: 16, fill: '#BFC1C3' }} />
                </button>
                <div>
                  <H3
                    css={{
                      color: '#333',
                      fontFamily: 'Golos',
                      fontSize: 18,
                      marginBottom: 10,
                    }}
                  >
                    Категория:
                  </H3>
                  {categories.map(
                    (category) =>
                      category.count > 0 && (
                        <CollapsableRotateAnimate
                          key={`CollapsableRotateAnimate${category.id}`}
                          isCollapse={openedCategory === category.id}
                          callback={() =>
                            setOpenedCategory(
                              openedCategory === category.id
                                ? null
                                : category.id
                            )
                          }
                          head={
                            <>
                              <H4
                                css={{
                                  margin: '8px 0',
                                  fontSize: 12,
                                  color: '#393939',
                                }}
                              >
                                {category.title}
                              </H4>
                              <a
                                key={category.slug}
                                className='hide'
                                href={[
                                  GB_DOMAIN,
                                  currentCity.url,
                                  'catalog',
                                  category.slug,
                                  '',
                                ].join('/')}
                              >
                                {category.title}
                              </a>
                            </>
                          }
                          body={
                            <div
                              css={{
                                fontSize: 12,
                                color: '#333',
                                label: {
                                  margin: '4px 0',
                                },
                              }}
                            >
                              <CustomCheckBox
                                value={String(category.id)}
                                callback={() =>
                                  checkCategory(category, category.children)
                                }
                                label={`Все ${category.count}`}
                                checked={
                                  query.filters.categories.category ===
                                    category.id &&
                                  query.filters.categories.subcategories
                                    .length === category.children.length
                                }
                                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                // @ts-ignore
                                appendElement={
                                  <a
                                    className='hide'
                                    href={[
                                      GB_DOMAIN,
                                      currentCity.url,
                                      'catalog',
                                      category.slug,
                                      '',
                                    ].join('/')}
                                  >
                                    Все {category.count}
                                  </a>
                                }
                              />
                              {category.children.map(
                                (subcategory) =>
                                  subcategory.count > 0 && (
                                    <CustomCheckBox
                                      key={`CustomCheckBox__${subcategory.id}`}
                                      value={String(subcategory.id)}
                                      callback={() =>
                                        checkSubcategory(
                                          category.id,
                                          subcategory
                                        )
                                      }
                                      label={`${subcategory.title} ${subcategory.count}`}
                                      checked={
                                        query.filters.categories.subcategories.indexOf(
                                          subcategory.id
                                        ) !== -1
                                      }
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-ignore
                                      appendElement={
                                        <a
                                          className='hide'
                                          href={[
                                            GB_DOMAIN,
                                            currentCity.url,
                                            'catalog',
                                            category.slug,
                                            subcategory.slug,
                                            '',
                                          ].join('/')}
                                        >
                                          {subcategory.title}{' '}
                                          {subcategory.count}
                                        </a>
                                      }
                                    />
                                  )
                              )}
                            </div>
                          }
                        />
                      )
                  )}
                </div>
                {currentCity.country.is_default && (
                  <div>
                    <H3 css={{ color: '#333', margin: '0 0 8px' }}>
                      Стоимость:
                    </H3>
                    <div
                      css={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        fontSize: 12,
                        marginLeft: '-10px',
                      }}
                    >
                      {filterPriceArr.map((item) => (
                        <ButtonFilterCatalog
                          isActive={
                            !Array.isArray(query.filters.filter_price__gte) &&
                            Number(query.filters.filter_price__gte) ===
                              Number(item.price1)
                          }
                          key={`priceFilter${item.id}`}
                          callback={() =>
                            setQuery({
                              ...query,
                              filters: {
                                ...query.filters,
                                filter_price__gte:
                                  query.filters.filter_price__gte ===
                                  item.price1
                                    ? []
                                    : item.price1,
                                filter_price__lte:
                                  query.filters.filter_price__lte ===
                                  item.price2
                                    ? []
                                    : item.price2,
                                pages: [1, 1],
                              },
                              isDataFromServer: false,
                            })
                          }
                        >
                          {item.title}
                        </ButtonFilterCatalog>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <H3 css={{ color: '#333', margin: '0 0 8px' }}>Когда:</H3>
                  <div
                    css={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      fontSize: 12,
                      marginLeft: '-10px',
                    }}
                  >
                    {filterDateArr.map((item) => (
                      <ButtonFilterCatalog
                        isActive={query.dateFilterId === item.id}
                        key={`dateFilter${item.id}`}
                        callback={() =>
                          query.dateFilterId === item.id
                            ? onChangeDateFilter()
                            : onChangeDateFilter(
                                item.id,
                                ...filterDates[item.id]
                              )
                        }
                      >
                        {item.title}
                      </ButtonFilterCatalog>
                    ))}
                  </div>
                  <div
                    // filter__calendar filter__calendar_desktop
                    css={{
                      // marginLeft: 0,
                      marginTop: 4.5,
                      marginBottom: 4.5,
                      position: 'relative',
                    }}
                  >
                    <div
                      // filter__dropdown
                      css={{}}
                      ref={outsideRefCalendar as Ref<HTMLDivElement>}
                    >
                      <div
                        // filter__calendar-btn
                        css={{
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          boxSizing: 'border-box',
                          fontFamily: `"Golos", Arial`,
                          background: 'transparent',
                          color: theme.colors.textSecondary,
                          borderRadius: 0,
                          fontWeight: 500,
                          lineHeight: 1,
                        }}
                        onClick={() => setCalendarIsOpen(!calendarIsOpen)}
                      >
                        Дата
                        <svg
                          css={{
                            width: 12,
                            height: 12,
                            marginLeft: 10,
                            overflow: 'hidden',
                          }}
                          // icon icon_xsm icon_ml
                        >
                          <use
                            xlinkHref={
                              calendarIsOpen ? '#triangle-up' : '#triangle-down'
                            }
                          />
                        </svg>
                      </div>
                      {calendarIsOpen && (
                        <div
                          css={{
                            position: 'absolute',
                            width: 400,
                            left: -15,
                            top: 25,
                            zIndex: 99,
                            background: theme.colors.white,
                            boxShadow: `0px 4px 15px rgba(0, 0, 0, 0.2)`,
                          }}
                          // filter__dropdown-body
                        >
                          <Calendar
                            onDatePicked={(values) =>
                              onCalendarDateChanged(values)
                            }
                            enableClear
                            additionalCss={{ padding: '24px 0' }}
                          />
                          <div
                            // filter__calendar-btn
                            css={{
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              boxSizing: 'border-box',
                              fontFamily: `"Golos", Arial`,
                              background: 'transparent',
                              color: theme.colors.textSecondary,
                              borderRadius: 0,
                              fontWeight: 500,
                              lineHeight: 1,
                              paddingLeft: 10,

                              width: '100%',
                              display: 'flex',
                              justifyContent: 'center',
                              marginBottom: 20,
                              span: {
                                height: 40,
                              },
                            }}
                          >
                            <span
                              // btn-v2   btn-v2-shadow
                              css={{
                                textAlign: 'center',
                                boxShadow: `0px 0px 10px 0px rgba(0, 0, 0, 0.25)`,
                                fontFamily: 'Golos',
                                fontSize: 16,
                                fontStyle: 'normal',
                                fontWeight: 500,
                                lineHeight: '16px',
                                letterSpacing: '0em',
                                border: 'none',
                                cursor: 'pointer',
                                textDecoration: 'none',
                                color: theme.colors.white,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '1.28em',
                                padding: '0 1.6em',
                                backgroundColor: theme.colors.primary,
                                backgroundImage: `linear-gradient(120deg, #ff4a2e 0%, ${theme.colors.gradient} 100%)`,
                                backgroundSize: `500% 500%`,
                                margin: `0 auto`,
                                animation:
                                  'move-background 60s linear infinite',
                                transition: `all 0.2s ease, box-shadow 0.4s ease`,
                                '&:hover': {
                                  boxShadow: `0px 0px 10px 0px ${theme.colors.gradient}`,
                                },
                              }}
                              onClick={() => onCalendarSubmit(calendarDates)}
                            >
                              {totalCalendarPosts !== null &&
                              totalCalendarPosts >= 0
                                ? dataForCalendarButtonIsLoading
                                  ? 'Загрузка...'
                                  : `Показать ${totalCalendarPosts} 
                              ${getEventTextFromCount(totalCalendarPosts)}`
                                : 'Показать события'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div
                css={{
                  width: '75%',
                  [DEVICE_SCREENS_MAX_STRING.lg]: {
                    width: '100%',
                  },
                }}
                data-testid='eventList'
              >
                <div
                  css={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    gap: 20,
                    marginBottom: 20,
                    padding: '0 8px',
                    [DEVICE_SCREENS_MAX_STRING.md]: {
                      gap: 8,
                    },
                    [DEVICE_SCREENS_MAX_STRING.sm]: {
                      display: 'none',
                    },
                  }}
                  data-testid='sortButtons'
                >
                  {sortTypes?.map((item) => (
                    <ButtonOrderingCatalog
                      onClick={() => {
                        setQuery({
                          ...query,
                          filters: {
                            ...query.filters,
                            sort:
                              item.slug === query.filters.sort ? [] : item.slug,
                            pages: [1, 1],
                          },
                          isDataFromServer: false,
                        });
                      }}
                      key={item.value}
                      isActive={item.slug === query.filters.sort}
                    >
                      {item.description}
                    </ButtonOrderingCatalog>
                  ))}
                </div>
                {events.results && events.results.length ? (
                  <>
                    <div
                      // actions
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      css={{
                        ...theme.mixins.flex(),
                      }}
                    >
                      {events.results.map((item) => {
                        if (isPromoCard(item)) {
                          return (
                            <PromoCard
                              key={`PromoCard__${item.id}`}
                              item={item}
                            />
                          );
                        }
                        return (
                          <PostCard
                            catalog
                            key={`PostCard__${item.id}`}
                            item={item}
                          />
                        );
                      })}
                    </div>
                    {events.next && (
                      <div
                        css={{
                          display: 'flex',
                          gap: 10,
                          padding: '0 8px',
                          [DEVICE_SCREENS_MAX_STRING.md]: {
                            paddingTop: 10,
                          },
                        }}
                      >
                        <ButtonShowMore
                          callback={() => {
                            setIsNewData(true);
                            setQuery({
                              ...query,
                              filters: {
                                ...query.filters,
                                pages:
                                  query.filters.pages[1] &&
                                  query.filters.pages[0]
                                    ? [
                                        Number(query.filters.pages[0]),
                                        Number(query.filters.pages[1]) + 1,
                                      ]
                                    : [1, 2],
                              },
                              isDataFromServer: false,
                            });
                          }}
                        />
                        <StandardButton callback={() => window.scrollTo(0, 0)}>
                          Наверх
                        </StandardButton>
                      </div>
                    )}
                    <div
                      css={{
                        padding: '0 8px',
                        [DEVICE_SCREENS_MAX_STRING.md]: {
                          padding: '4px 8px',
                        },
                      }}
                    >
                      <Pagination
                        currentPage={
                          query.filters.pages.length === 2
                            ? query.filters.pages[1] - 1
                            : 0
                        }
                        onPrevPage={() => {
                          updateLoading(true);
                          setQuery({
                            ...query,
                            filters: {
                              ...query.filters,
                              pages:
                                query.filters.pages.length === 2 &&
                                Number(query.filters.pages[1] - 1) !== 0
                                  ? [
                                      Number(query.filters.pages[1] - 1),
                                      Number(query.filters.pages[1] - 1),
                                    ]
                                  : [1, 1],
                            },
                            isDataFromServer: false,
                          });
                        }}
                        onNextPage={() => {
                          updateLoading(true);
                          setQuery({
                            ...query,
                            filters: {
                              ...query.filters,
                              pages:
                                query.filters.pages.length === 2 &&
                                Number(query.filters.pages[1] + 1) <
                                  getCountOfPage(events.count)
                                  ? [
                                      Number(query.filters.pages[1] + 1),
                                      Number(query.filters.pages[1] + 1),
                                    ]
                                  : [
                                      getCountOfPage(events.count),
                                      getCountOfPage(events.count),
                                    ],
                            },
                            isDataFromServer: false,
                          });
                        }}
                        count={getCountOfPage(events.count)}
                        changePage={(event: any) => {
                          updateLoading(true);
                          setQuery({
                            ...query,
                            filters: {
                              ...query.filters,
                              pages: [
                                (event.selected ??
                                  event.currentTarget.dataset.page) + 1,
                                (event.selected ??
                                  event.currentTarget.dataset.page) + 1,
                              ],
                            },
                            isDataFromServer: false,
                          });
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div
                    css={{
                      fontFamily: 'Montserrat',
                      width: '100%',
                    }}
                  >
                    <div
                      css={{
                        minHeight: 400,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginTop: 60,
                      }}
                    >
                      <LoupSVG css={{ marginBottom: 30 }} />
                      <div
                        css={{
                          marginBottom: 4,
                          fontSize: 18,
                          fontWeight: 'bold',
                          color: '#333',
                        }}
                      >
                        К сожалению, ничего не нашлось.
                      </div>
                      <div
                        css={{
                          fontSize: 12,
                          color: '#333',
                          marginBottom: 120,
                        }}
                      >
                        Попробуйте изменить варианты или фильтры поиска
                      </div>
                    </div>
                    <CatalogRecommends
                      title='Вам может быть интересно'
                      viewed={gorbiletRekomenduet}
                      currentCity={currentCity}
                    />
                  </div>
                )}
              </div>
            </div>
            <div
              css={{
                display: 'flex',
                justifyContent: 'flex-end',
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  display: 'none',
                },
              }}
            >
              <div css={{ width: '75%' }}>
                {seoCategory && query.filters.pages.length === 0 && (
                  <CropText
                    text={seoCategory.description}
                    deskTopCount={1000}
                    mobileCount={500}
                  />
                )}
              </div>
            </div>

            <UpButton screenCount={1.5} mobileScreenCount={1} />

            <div
              css={{
                position: 'fixed',
                bottom: showPopUp ? 67 : 15,
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                zIndex: 999,
              }}
            >
              <button
                type='button'
                css={{
                  width: 270,
                  height: 40,
                  borderRadius: 19,
                  fontSize: 13,
                  boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.25)',
                  backgroundColor: '#ff4a2e',
                  backgroundImage:
                    'linear-gradient(120deg, #ff4a2e 0%, #de2839 100%)',
                  backgroundSize: '500% 500%',
                  animation: 'move-background 60s linear infinite',
                  transition: 'all 0.2s ease, box-shadow 0.4s ease',
                  color: '#fff',
                  display: 'none',
                  [DEVICE_SCREENS_MAX_STRING.md]: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                  },
                }}
                onClick={() => setShowMobileSort(true)}
              >
                <span css={{ width: 180 }}>Фильтры и сортировка</span>

                <span css={{ position: 'relative' }}>
                  <svg css={{ width: 16, height: 16, fill: '#fff' }}>
                    <use xlinkHref='#filter' />
                  </svg>
                  {!!mobileFiltersCount && (
                    <span
                      css={{
                        width: 16,
                        height: 16,
                        position: 'absolute',
                        top: -5,
                        right: -14,
                        background: '#fff',
                        borderRadius: '50%',
                        color: '#F44336',
                        fontSize: 11.5,
                      }}
                    >
                      {mobileFiltersCount}
                    </span>
                  )}
                </span>
              </button>
            </div>
          </main>
        </>
      )}
      <div
        css={{
          display: 'flex',
          justifyContent: 'flex-end',
          [DEVICE_SCREENS_MAX_STRING.md]: {
            display: 'none',
          },
        }}
      >
        <div css={{ width: '75%' }}>
          {currentCity && query.filters.pages.length === 0 && (
            <CropText
              text={currentCity.short_description}
              deskTopCount={1000}
              mobileCount={500}
            />
          )}
        </div>
      </div>

      <div
        css={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 16,
          marginBottom: '4rem',
          marginTop: '2rem',
          [DEVICE_SCREENS_MAX_STRING.xl]: {
            gridTemplateColumns: '1fr',
          },
          [DEVICE_SCREENS_MAX_STRING.md]: {
            display: 'none',
          },
        }}
      >
        <Subscription currentCity={currentCity} />
        <MobileApps />
      </div>

      {typeof query.filters.categories.category === 'number' && (
        <div
          css={{
            display: 'none',
            [DEVICE_SCREENS_MAX_STRING.md]: {
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              marginBottom: '4rem',
              marginTop: '2rem',
              padding: '0 8px',
            },
          }}
        >
          <Subscription currentCity={currentCity} />
          <MobileApps />
        </div>
      )}
    </Container>
  );
}
